import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import FilterActionWrapper from '../FilterActionWrapper';
import { useTranslation } from 'react-i18next';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useOperationStepValues from 'router/data/useOperationStepValues';
import utils from 'utils';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import ListingCreationSelect from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelect';
import { Button } from 'ncoded-component-library';
import useCallbackRef from 'hooks/useCallbackRef';
import useQueryFilter from 'hooks/useQueryFilter';
import { ListingQueryFilterParams } from 'router/subrouters/Dash/subrouters/hooks/useBusinessesFilters';

import './LocationFilter.styles.scss';
import './LocationFilter.styles.responsive.scss';

type LocationFilterProps = {
  className?: string;
  title?: string;
  showIcon?: boolean;
};

export const LOCATION_FILTER_KEY = 'location';

const LocationFilter: React.FC<LocationFilterProps> = (props) => {
  const { className, title, showIcon = true } = props;

  const { t } = useTranslation();
  const {
    params: { location },
    onFiltersApply,
  } = useQueryFilter<ListingQueryFilterParams>();
  const { locationOptions } = useOperationStepValues(true);
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => (location ? utils.mapParamsToArray(location) : []),
    [location],
  );
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [currentState, setCurrentState] = useState(defaultVal);

  const classes = classNames('location-filter', className);

  const onSelectFilters = useCallback(
    (options: string[]) => {
      dropdown.setIsOpen(false);
      setButtonDisabled(true);

      onFiltersApply(LOCATION_FILTER_KEY, options);
    },
    [dropdown, onFiltersApply],
  );

  const triggerTitle = useMemo(() => {
    return location
      ? utils.stringifyFilterValues(location)
      : title || t('locations');
  }, [location, t, title]);

  useEffect(() => {
    setCurrentState(defaultVal);
  }, [defaultVal]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      onClose={() => {
        setCurrentState(defaultVal);
        setButtonDisabled(true);
      }}
      {...(showIcon && { icon: LocationmarkerIcon })}
    >
      <ListingCreationSelect
        value={currentState}
        options={locationOptions}
        onChange={(v) => {
          setButtonDisabled(false);
          setCurrentState(v);
        }}
        columns={locationOptions.length}
        withShowMore={false}
        styleType="secondary"
      />

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={buttonDisabled}
          onClick={() => onSelectFilters(currentState)}
        >
          {t('applyLocationFilter')}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default LocationFilter;
